import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const EmployeeTable = ({ 
  employees, 
  selectedEmployees, 
  onSelectEmployee, 
  onSelectAll, 
  onEditEmployee, 
  onManageAvailability, 
  onViewSchedule,
  onDeleteEmployee 
}) => {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedEmployees = () => {
    return [...employees]?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];
      
      if (sortField === 'services') {
        aValue = a?.services?.length;
        bValue = b?.services?.length;
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { label: 'در دسترس', color: 'bg-success text-success-foreground' },
      busy: { label: 'مشغول', color: 'bg-warning text-warning-foreground' },
      unavailable: { label: 'غیرفعال', color: 'bg-error text-error-foreground' },
      break: { label: 'استراحت', color: 'bg-secondary text-secondary-foreground' }
    };

    const config = statusConfig?.[status] || statusConfig?.unavailable;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const sortedEmployees = getSortedEmployees();
  const isAllSelected = employees?.length > 0 && selectedEmployees?.length === employees?.length;
  const isPartiallySelected = selectedEmployees?.length > 0 && selectedEmployees?.length < employees?.length;

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="w-12 p-4 text-right">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="p-4 text-right">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  نام کارمند
                  <Icon 
                    name={sortField === 'name' && sortDirection === 'desc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="p-4 text-right">
                <span className="text-sm font-medium text-foreground">اطلاعات تماس</span>
              </th>
              <th className="p-4 text-right">
                <button
                  onClick={() => handleSort('services')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  خدمات تخصصی
                  <Icon 
                    name={sortField === 'services' && sortDirection === 'desc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="p-4 text-right">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
                >
                  وضعیت
                  <Icon 
                    name={sortField === 'status' && sortDirection === 'desc' ? 'ChevronUp' : 'ChevronDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="p-4 text-right">
                <span className="text-sm font-medium text-foreground">عملیات</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEmployees?.map((employee) => (
              <tr key={employee?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="p-4">
                  <Checkbox
                    checked={selectedEmployees?.includes(employee?.id)}
                    onChange={(e) => onSelectEmployee(employee?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-foreground">
                        {employee?.name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{employee?.name}</p>
                      <p className="text-xs text-muted-foreground">کد: {employee?.employeeCode}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <p className="text-sm text-foreground">{employee?.phone}</p>
                    <p className="text-xs text-muted-foreground">{employee?.email}</p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {employee?.services?.slice(0, 2)?.map((service, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-accent/10 text-accent rounded-full text-xs"
                      >
                        {service}
                      </span>
                    ))}
                    {employee?.services?.length > 2 && (
                      <span className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs">
                        +{employee?.services?.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  {getStatusBadge(employee?.status)}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditEmployee(employee)}
                      iconName="Edit"
                      iconSize={16}
                    >
                      <span className="sr-only">ویرایش</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onManageAvailability(employee)}
                      iconName="Calendar"
                      iconSize={16}
                    >
                      <span className="sr-only">مدیریت دسترسی</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewSchedule(employee)}
                      iconName="Clock"
                      iconSize={16}
                    >
                      <span className="sr-only">مشاهده برنامه</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteEmployee(employee)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-error hover:text-error hover:bg-error/10"
                    >
                      <span className="sr-only">حذف</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-4 p-4">
        {sortedEmployees?.map((employee) => (
          <div key={employee?.id} className="bg-muted rounded-lg p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  checked={selectedEmployees?.includes(employee?.id)}
                  onChange={(e) => onSelectEmployee(employee?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {employee?.name?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{employee?.name}</p>
                  <p className="text-xs text-muted-foreground">کد: {employee?.employeeCode}</p>
                </div>
              </div>
              {getStatusBadge(employee?.status)}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={14} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{employee?.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={14} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{employee?.email}</span>
              </div>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-2">خدمات تخصصی:</p>
              <div className="flex flex-wrap gap-1">
                {employee?.services?.map((service, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-accent/10 text-accent rounded-full text-xs"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditEmployee(employee)}
                iconName="Edit"
                iconSize={14}
              >
                ویرایش
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onManageAvailability(employee)}
                iconName="Calendar"
                iconSize={14}
              >
                دسترسی
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteEmployee(employee)}
                iconName="Trash2"
                iconSize={14}
                className="text-error hover:text-error hover:bg-error/10"
              >
                حذف
              </Button>
            </div>
          </div>
        ))}
      </div>
      {employees?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-foreground font-medium mb-2">هیچ کارمندی یافت نشد</p>
          <p className="text-sm text-muted-foreground">برای شروع، کارمند جدیدی اضافه کنید</p>
        </div>
      )}
    </div>
  );
};

export default EmployeeTable;